import React, { useEffect, useState, useContext } from "react";
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { makeStyles } from "@material-ui/core/styles";
import { Add,
  DeleteOutline,
  Edit } from "@material-ui/icons";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import {
  KoraxPage,
  KoraxPageHeader
} from "../../components/KoraxUI";

const currency = v => Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const useStyles = makeStyles(theme => ({
  root: {
    minHeight: "calc(100vh - 56px)",
    paddingBottom: theme.spacing(8),
    overflowY: "auto",
    background: "linear-gradient(145deg,#EEF5FF 0%,#F8FAFC 100%)",

    [theme.breakpoints.down("sm")]: {
      paddingBottom: theme.spacing(9)
    }
  },
  hero: {
    borderRadius: 28,
    padding: theme.spacing(2.4),
    background: "linear-gradient(135deg,#FFFFFF 0%,#EAF3FF 55%,#F8FAFC 100%)",
    border: "1px solid #E2E8F0",
    boxShadow: "0 22px 55px rgba(0,102,255,.11)",
    display: "grid",
    gridTemplateColumns: "1fr auto",
    alignItems: "center",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(1.5),
    [theme.breakpoints.down("sm")]: { gridTemplateColumns: "1fr" }
  },
  heroIcon: {
    width: 58, height: 58, borderRadius: 20,
    background: "linear-gradient(135deg,#0066FF,#00A3FF)",
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: 28, color: "#fff", boxShadow: "0 10px 24px rgba(0,102,255,.28)"
  },
  heroLeft: { display: "flex", alignItems: "center", gap: theme.spacing(1.4) },
  title: { fontWeight: 950, fontSize: "1.55rem", color: "#0F172A" },
  subtitle: { color: "#64748B", fontSize: ".84rem", marginTop: 6 },
  addBtn: {
    borderRadius: 16, fontWeight: 900, textTransform: "none",
    background: "linear-gradient(135deg,#0066FF,#00A3FF)",
    color: "#fff", padding: theme.spacing(1, 2),
    boxShadow: "0 12px 26px rgba(0,102,255,.22)",
    "&:hover": { background: "linear-gradient(135deg,#0052CC,#007ACC)" },
    [theme.breakpoints.down("sm")]: { width: "100%" }
  },
  board: {
    borderRadius: 28,
    background: "#fff",
    border: "1px solid #E2E8F0",
    boxShadow: "0 18px 45px rgba(15,23,42,.06)",
    overflow: "hidden"
  },
  toolbar: {
    padding: theme.spacing(1.4),
    borderBottom: "1px solid #E2E8F0",
    display: "grid",
    gridTemplateColumns: "1fr 180px",
    gap: theme.spacing(1),
    [theme.breakpoints.down("sm")]: { gridTemplateColumns: "1fr" }
  },
  field: { "& .MuiOutlinedInput-root": { borderRadius: 16 } },
  list: { padding: theme.spacing(1.2), display: "grid", gap: theme.spacing(1) },
  card: {
    borderRadius: 22,
    border: "1px solid #E2E8F0",
    background: "#fff",
    padding: theme.spacing(1.3),
    display: "grid",
    gridTemplateColumns: "1fr auto",
    gap: theme.spacing(1),
    boxShadow: "0 10px 26px rgba(15,23,42,.045)",
    [theme.breakpoints.down("sm")]: { gridTemplateColumns: "1fr" }
  },
  cardTitle: { fontWeight: 950, color: "#0F172A", fontSize: "1rem" },
  cardDesc: { color: "#64748B", fontSize: ".78rem", marginTop: 5, lineHeight: 1.45 },
  meta: { display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 },
  actions: { display: "flex", gap: 6, alignItems: "center", justifyContent: "flex-end" },
  iconBtn: {
    width: 38, height: 38, borderRadius: 13,
    border: "1px solid #E2E8F0", background: "#F8FAFC",
    cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center"
  },
  dialogPaper: { borderRadius: 28, overflow: "hidden" },
  dialogTitle: {
    background: "linear-gradient(135deg,#071B4D,#0066FF,#00A3FF)",
    color: "#fff", padding: theme.spacing(2)
  }
}));

const emptyForm = {
  title: "",
  description: "",
  status: "open",
  priority: "normal",
  value: "",
  scheduledAt: "",
  notes: "",
  contactId: null
};

const statusLabel = {
  open: "Aberta",
  progress: "Em execução",
  waiting: "Aguardando",
  done: "Finalizada",
  canceled: "Cancelada"
};

const ServiceOrderModal = ({ open, onClose, order, reload }) => {
  const classes = useStyles();
  const [form, setForm] = useState(emptyForm);
  const [contacts, setContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (order) {
      setForm({
        ...emptyForm,
        ...order,
        scheduledAt: order.scheduledAt ? String(order.scheduledAt).slice(0, 16) : ""
      });
      setSelectedContact(order.contact || null);
    } else {
      setForm(emptyForm);
      setSelectedContact(null);
    }
  }, [order, open]);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const searchContacts = async value => {
    if (!value || value.length < 2) return;
    try {
      const { data } = await api.get("/contacts", { params: { searchParam: value } });
      setContacts(data.contacts || []);
    } catch (err) {
      toastError(err);
    }
  };

  const save = async () => {
    if (!form.title.trim()) {
      toast.error("Informe o título da OS.");
      return;
    }

    try {
      setSaving(true);
      const payload = {
        ...form,
        contactId: selectedContact?.id || null,
        value: form.value || 0,
        scheduledAt: form.scheduledAt || null
      };

      if (order?.id) await api.put(`/service-orders/${order.id}`, payload);
      else await api.post("/service-orders", payload);

      toast.success(order?.id ? "OS atualizada." : "OS criada.");
      reload();
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" classes={{ paper: classes.dialogPaper }}>
      <DialogTitle className={classes.dialogTitle}>
        <Typography style={{ fontWeight: 950 }}>{order?.id ? "Editar Ordem de Serviço" : "Nova Ordem de Serviço"}</Typography>
        <Typography style={{ opacity: .85, fontSize: ".8rem" }}>Registre uma solicitação ligada ao cliente e ao atendimento.</Typography>
      </DialogTitle>

      <DialogContent style={{ padding: 22 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Autocomplete
              options={contacts}
              getOptionLabel={option => `${option.name || ""} ${option.number ? `(${option.number})` : ""}`}
              value={selectedContact}
              onChange={(e, value) => setSelectedContact(value)}
              onInputChange={(e, value) => searchContacts(value)}
              renderInput={params => <TextField {...params} className={classes.field} label="Cliente" variant="outlined" fullWidth />}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField className={classes.field} label="Título da OS" variant="outlined" fullWidth value={form.title} onChange={e => set("title", e.target.value)} />
          </Grid>

          <Grid item xs={12}>
            <TextField className={classes.field} label="Descrição do serviço/solicitação" variant="outlined" fullWidth multiline rows={4} value={form.description || ""} onChange={e => set("description", e.target.value)} />
          </Grid>

          <Grid item xs={12} sm={4}>
            <FormControl variant="outlined" fullWidth className={classes.field}>
              <InputLabel>Status</InputLabel>
              <Select value={form.status} label="Status" onChange={e => set("status", e.target.value)}>
                <MenuItem value="open">Aberta</MenuItem>
                <MenuItem value="progress">Em execução</MenuItem>
                <MenuItem value="waiting">Aguardando</MenuItem>
                <MenuItem value="done">Finalizada</MenuItem>
                <MenuItem value="canceled">Cancelada</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={4}>
            <FormControl variant="outlined" fullWidth className={classes.field}>
              <InputLabel>Prioridade</InputLabel>
              <Select value={form.priority} label="Prioridade" onChange={e => set("priority", e.target.value)}>
                <MenuItem value="low">Baixa</MenuItem>
                <MenuItem value="normal">Normal</MenuItem>
                <MenuItem value="high">Alta</MenuItem>
                <MenuItem value="urgent">Urgente</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField className={classes.field} label="Valor" type="number" variant="outlined" fullWidth value={form.value || ""} onChange={e => set("value", e.target.value)} />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField className={classes.field} label="Data prevista" type="datetime-local" variant="outlined" fullWidth value={form.scheduledAt || ""} onChange={e => set("scheduledAt", e.target.value)} InputLabelProps={{ shrink: true }} />
          </Grid>

          <Grid item xs={12}>
            <TextField className={classes.field} label="Observações internas" variant="outlined" fullWidth multiline rows={3} value={form.notes || ""} onChange={e => set("notes", e.target.value)} />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions style={{ padding: 18 }}>
        <Button onClick={onClose} disabled={saving} style={{ borderRadius: 14, fontWeight: 900, textTransform: "none" }}>Cancelar</Button>
        <Button onClick={save} disabled={saving} className={classes.addBtn}>{saving ? "Salvando..." : "Salvar OS"}</Button>
      </DialogActions>
    </Dialog>
  );
};

const ServiceOrders = () => {
  const classes = useStyles();
  const { user, socket } = useContext(AuthContext);
  const [orders, setOrders] = useState([]);
  const [searchParam, setSearchParam] = useState("");
  const [status, setStatus] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const fetchOrders = async () => {
    try {
      const { data } = await api.get("/service-orders", { params: { searchParam, status } });
      setOrders(data || []);
    } catch (err) {
      toastError(err);
    }
  };

  useEffect(() => { fetchOrders(); }, [searchParam, status]);

  useEffect(() => {
    if (!socket || !user?.companyId) return;
    const eventName = `company${user.companyId}-service-order`;
    const onOrder = () => fetchOrders();
    socket.on(eventName, onOrder);
    return () => socket.off(eventName, onOrder);
  }, [socket, user?.companyId]);

  const openNew = () => {
    setSelectedOrder(null);
    setModalOpen(true);
  };

  const remove = async order => {
    if (!window.confirm(`Excluir OS "${order.title}"?`)) return;
    try {
      await api.delete(`/service-orders/${order.id}`);
      toast.success("OS removida.");
      fetchOrders();
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <KoraxPage className={classes.root}>
      <ServiceOrderModal open={modalOpen} onClose={() => setModalOpen(false)} order={selectedOrder} reload={fetchOrders} />

      <KoraxPageHeader
        title="Ordens de Serviço"
        subtitle="Crie, acompanhe e organize serviços ligados aos seus clientes e atendimentos."
        actions={
          <Button
            className={classes.addBtn}
            startIcon={<Add />}
            onClick={openNew}
          >
            Nova OS
          </Button>
        }
      />

      <main className={classes.board}>
        <div className={classes.toolbar}>
          <TextField className={classes.field} variant="outlined" size="small" placeholder="Buscar OS..." value={searchParam} onChange={e => setSearchParam(e.target.value)} />
          <FormControl className={classes.field} variant="outlined" size="small">
            <InputLabel>Status</InputLabel>
            <Select value={status} label="Status" onChange={e => setStatus(e.target.value)}>
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="open">Aberta</MenuItem>
              <MenuItem value="progress">Em execução</MenuItem>
              <MenuItem value="waiting">Aguardando</MenuItem>
              <MenuItem value="done">Finalizada</MenuItem>
              <MenuItem value="canceled">Cancelada</MenuItem>
            </Select>
          </FormControl>
        </div>

        <div className={classes.list}>
          {orders.map(order => (
            <article key={order.id} className={classes.card}>
              <div>
                <Typography className={classes.cardTitle}>#{order.id} — {order.title}</Typography>
                <Typography className={classes.cardDesc}>{order.description || "Sem descrição."}</Typography>
                <div className={classes.meta}>
                  <Chip size="small" label={statusLabel[order.status] || order.status} style={{ fontWeight: 900 }} />
                  <Chip size="small" label={order.priority || "normal"} />
                  {order.contact?.name && <Chip size="small" label={`Cliente: ${order.contact.name}`} />}
                  <Chip size="small" label={currency(order.value)} />
                </div>
              </div>

              <div className={classes.actions}>
                <button className={classes.iconBtn} onClick={() => { setSelectedOrder(order); setModalOpen(true); }}><Edit fontSize="small" /></button>
                <button className={classes.iconBtn} onClick={() => remove(order)} style={{ color: "#EF4444" }}><DeleteOutline fontSize="small" /></button>
              </div>
            </article>
          ))}

          {!orders.length && (
            <div style={{ padding: 30, textAlign: "center", color: "#64748B", fontWeight: 900 }}>
              Nenhuma OS criada ainda.
            </div>
          )}
        </div>
      </main>
    </KoraxPage>
  );
};

export default ServiceOrders;
